import { resolveApiUrl } from "@/utils/apiUrl";
import { clientFingerprintHeaders } from "@/utils/clientFingerprint";
import { nativeAppRequestHeaders } from "@/utils/nativeApp";

export interface MemberProfile {
  id: number;
  userName: string;
  nickName: string;
  email: string;
  avatar: string;
  shareCode: string;
  isDealer: boolean;
  pointsBalance: number;
  hasGlobalFirstEpisodePass: boolean;
}

export interface DramaEpisode {
  id: number;
  categoryId?: number;
  categoryName?: string;
  categoryCode?: string;
  episodeNo: number;
  title: string;
  coverUrl?: string;
  videoUrl: string;
  quality?: string;
  durationSeconds: number;
  unlockPoints: number;
  isUnlocked: boolean;
  subtitleTracks?: DramaSubtitleTrack[];
}

export interface DramaSubtitleTrack {
  id: number;
  episodeId: number;
  languageCode: string;
  languageName: string;
  sourceLanguage?: string;
  kind: "subtitles" | "captions";
  isDefault: boolean;
  version: number;
  url: string;
}

export interface DramaCard {
  id: number;
  sourceType?: "platform" | "creator";
  title: string;
  categoryId?: number;
  categoryName?: string;
  categoryCode?: string;
  coverUrl: string;
  carouselImageUrl?: string;
  intro: string;
  shareTitle: string;
  shareDesc: string;
  isCarousel?: boolean;
  isHot?: boolean;
  hotRank?: number;
  viewCount?: number;
  weekViewCount?: number;
  monthViewCount?: number;
  favoriteCount?: number;
  createdAt?: string;
  updatedAt?: string;
  latestEpisodeAt?: string;
  totalEpisodes: number;
  unlockedCount: number;
  isFavorite: boolean;
  lastEpisodeId: number;
  lastEpisodeNo: number;
  lastEpisodeTitle: string;
  currentEpisode?: DramaEpisode;
}

export interface DramaDetail extends DramaCard {
  episodes: DramaEpisode[];
  nextUnlockEpisodeId: number;
  nextUnlockEpisodeNo: number;
  nextUnlockPoints: number;
  allUnlocked: boolean;
  hasGlobalFirstEpisodePass: boolean;
}

export interface MemberDramaLibrary {
  recent: DramaCard[];
  favorites: DramaCard[];
}

export interface CreatorWork {
  shareRatioBp: number;
  id: number;
  title: string;
  category: string;
  coverUrl: string;
  description: string;
  totalEpisodes: number;
  status: 'published' | 'review' | 'approved' | 'transcoding' | 'ready' | 'transcode_failed' | 'publish_failed' | 'rejected';
  submittedAt: string;
  publishedAt?: string;
  views: number;
  subscriptions: number;
  revenue: number;
  reviewRemark?: string;
  transcodeError?: string;
}

export interface CreatorDraft {
  title: string;
  categoryId: number;
  coverUrl: string;
  description: string;
	episodes: { num: number; title: string; duration: string; videoUrl: string; coverUrl?: string }[];
}

export interface CreatorProfile {
  userId: number;
  displayName: string;
  intro: string;
  avatar: string;
  since: string;
}

export interface CreatorCenterData {
  isCreator: boolean;
  profile?: CreatorProfile;
  works: CreatorWork[];
}

export interface EarningWallet {
  source: string;
  total: number;
  available: number;
  pending: number;
  withdrawn: number;
  minimum: number;
  feeRateBp: number;
}
export type EarningSource = 'distribution' | 'creator';
export async function fetchEarningWallet(token: string, source: EarningSource): Promise<EarningWallet> {
  return request(`/api/member/earnings/${source}`, { headers: buildHeaders(token) });
}
export async function fetchEarningWithdrawals(token: string, source: EarningSource, pageNum: number): Promise<{list: DistributorWithdrawal[]; total: number}> {
  return request(`/api/member/earnings/${source}/withdrawals?pageNum=${pageNum}`, { headers: buildHeaders(token) });
}
export async function requestEarningWithdrawal(token: string, source: EarningSource, payload: {amount: number; method: string; account: string}): Promise<DistributorWithdrawal> {
  return request(`/api/member/earnings/${source}/withdrawals`, {method: 'POST', headers: buildHeaders(token), body: JSON.stringify(payload)});
}

export interface MemberPointLog {
  id: number;
  changeType: string;
  changeAmount: number;
  balanceAfter: number;
  bizType: string;
  bizId: number;
  remark: string;
  createdAt?: string;
}

export interface MemberPointLogList {
  list: MemberPointLog[];
  total: number;
}

export interface DailyCheckInDay {
  day: number;
  rewardPoints: number;
  claimed: boolean;
  current: boolean;
}

export interface DailyCheckInStatus {
  signedInToday: boolean;
  streakDays: number;
  cycleDay: number;
  todayReward: number;
  grantedPoints: number;
  pointsBalance: number;
  totalEarned: number;
  days: DailyCheckInDay[];
}

export interface LimitedRechargeTaskStatus {
  taskCode: string;
  rechargeCount: number;
  targetCount: number;
  rewardPoints: number;
  startedAt: string;
  expiresAt: string;
  remainingSeconds: number;
  completed: boolean;
  rewarded: boolean;
  expired: boolean;
  grantedPoints: number;
}

export interface EmailBindingTaskStatus {
  taskCode: string;
  rewardPoints: number;
  completed: boolean;
  rewarded: boolean;
  grantedPoints: number;
}

export interface DailyUnlockTaskItem {
  taskCode: string;
  targetCount: number;
  rewardPoints: number;
  completed: boolean;
  rewarded: boolean;
}

export interface DailyUnlockTaskStatus {
  unlockCount: number;
  tasks: DailyUnlockTaskItem[];
  grantedPoints: number;
}

export interface DramaCategory {
  id: number;
  code: string;
  name: string;
  sort: number;
  count: number;
}

export interface DramaUnlockResult {
  drama: DramaDetail;
  pointsBalance: number;
  cost: number;
  alreadyUnlocked: boolean;
}

export interface RefreshTokenResult {
  accessToken: string;
  accessTokenExpires: number;
  refreshToken: string;
  refreshTokenExpires: number;
}

export interface MemberEmailCodeResult {
  email: string;
  expireSeconds: number;
}

export interface MemberLoginResult {
  user: MemberProfile;
  isNewUser: boolean;
  rewardPoints: number;
  appLoginRewardPoints?: number;
  accessToken: string;
  accessTokenExpires: number;
  refreshToken: string;
  refreshTokenExpires: number;
  loginStrategy: unknown;
}

export interface MemberAutoRegisterResult extends MemberLoginResult {
  account: string;
  password: string;
}

export interface CaptchaImageResult {
  captchaId: string;
  image: string;
}

export interface CaptchaConfigResult {
  captcha?: {
    open?: boolean;
    length?: number;
  };
}

export interface RechargePackage {
  id: string;
  title: string;
  amount: number;
  points: number;
  bonusPoints: number;
  currency: string;
  recommended: boolean;
  sort: number;
  status: number;
}

export interface PaymentMethodOption {
  id: "alipay" | "wechat" | "usdt";
  label: string;
  payType: string;
  minAmount: number;
  maxAmount: number;
  availablePackageIds: string[];
}

export interface MemberPaymentOptions {
  enabled: boolean;
  methods: PaymentMethodOption[];
  packages: RechargePackage[];
  usdtPackages: RechargePackage[];
  activityBonusMode: boolean;
  firstRechargeEligible: boolean;
  firstRechargeBonusPercent: number;
  firstRechargeBonusRemainingSeconds: number;
  rechargeBonusType: "first" | "daily" | "";
  rechargeBonusEligible: boolean;
  rechargeBonusPercent: number;
  rechargeBonusRemainingSeconds: number;
}

export interface HomeDisplayConfig {
  adImage: string;
  adUrl: string;
  mediaDomain: string;
  appDownloadUrl: string;
  appLoginRewardPoints: number;
  inviteRewardPoints: number;
  customerServiceEnabled?: boolean;
  customerServiceBaseUrl?: string;
  customerServiceTenantId?: string;
  customerServiceAppId?: string;
  customerServiceWebChannelId?: string;
  customerServiceAppChannelId?: string;
  creatorCenterEnabled?: boolean;
  creatorPlatformUrl?: string;
  availableLanguages?: Array<{
    code: string;
    label: string;
    shortLabel: string;
  }>;
  defaultLanguage?: string;
}

export interface PublicAdvertisement {
  id: number;
  title: string;
  imageUrl: string;
  targetUrl: string;
  description: string;
  placement: "content" | "home_nav" | "earn_recommend";
  displayType: "text" | "image";
}

export interface MemberRechargeCreateResult {
  outTradeNo: string;
  tradeNo: string;
  status: string;
  payType: string;
  amount: number;
  currency: string;
  points: number;
  packageTitle: string;
  payUrl: string;
  qrCode: string;
  pageUrl: string;
  expireTime?: string;
}

export interface MemberRechargeQueryResult {
  outTradeNo: string;
  tradeNo: string;
  channelTradeNo: string;
  status: string;
  payType: string;
  amount: number;
  currency: string;
  points: number;
  packageTitle: string;
  payUrl: string;
  qrCode: string;
  pageUrl: string;
  paidAt?: string;
  paidAmount: number;
  credited: boolean;
}

export interface MemberPaymentTicketResult {
	ticket: string;
	launchUrl: string;
	outTradeNo: string;
	expiresAt: string;
}

export interface MemberRechargeOrder {
  id: number;
  packageId: string;
  packageTitle: string;
  points: number;
  amount: number;
  currency: string;
  payType: string;
  outTradeNo: string;
  tradeNo: string;
  status: string;
  paidAmount: number;
  paidAt?: string;
  creditedAt?: string;
  createdAt?: string;
}

export interface DistributorProfile {
  id: number;
  userName: string;
  nickName: string;
  email: string;
  avatar: string;
  shareCode: string;
  firstLevelRatioBp: number;
  secondLevelRatioBp: number;
  baseFirstLevelRatioBp: number;
  baseSecondLevelRatioBp: number;
  totalCommission: number;
  availableBalance: number;
  pendingWithdrawAmount: number;
  withdrawnAmount: number;
}

export interface DistributorDailySummary {
  date: string;
  browseCount: number;
  registerCount: number;
  rechargeCount: number;
  rechargeAmount: number;
  commissionAmount: number;
}

export interface DistributorDashboard {
  profile: DistributorProfile;
  currentSummary: DistributorDailySummary;
  dailySummaries: DistributorDailySummary[];
}

export interface DistributorBrowseDetail {
  id: number;
  dramaId: number;
  dramaTitle: string;
  shareCode: string;
  channel: string;
  sourcePage: string;
  ipAddress: string;
  region: string;
  clickedCount: number;
  createdAt?: string;
}

export interface DistributorRegisterDetail {
  id: number;
  inviteeUserId: number;
  inviteeUserName: string;
  inviteeNickName: string;
  inviteeEmail: string;
  shareCode: string;
  rewardGranted: boolean;
  rewardGrantedAt?: string;
  createdAt?: string;
}

export interface DistributorRechargeDetail {
  id: number;
  orderId: number;
  orderNo: string;
  inviteeUserId: number;
  inviteeUserName: string;
  inviteeNickName: string;
  inviteeEmail: string;
  shareCode: string;
  level: number;
  payType: string;
  status: string;
  rechargeAmount: number;
  commissionAmount: number;
  ratioBasisPoint: number;
  paidAt?: string;
  createdAt?: string;
}

export interface DistributorWithdrawal {
  id: number;
  withdrawNo: string;
  amount: number;
  feeRateBp: number;
  feeAmount: number;
  netAmount: number;
  method: string;
  account: string;
  status: "pending" | "approved" | "rejected" | string;
  remark: string;
  processedAt?: string;
  availableAfter: number;
  createdAt?: string;
}

export class ApiError extends Error {
  status: number;

  constructor(message: string, status: number) {
    super(message);
    this.status = status;
  }
}

interface ApiEnvelope<T> {
  code: number;
  message: string;
  data: T;
}

function buildHeaders(token?: string): HeadersInit {
  const headers: HeadersInit = {
    "Content-Type": "application/json",
  };

  if (token) {
    headers.Authorization = `Bearer ${token}`;
  }

  Object.assign(headers, nativeAppRequestHeaders());

  return headers;
}

export interface MemberReferralOverview {
  shareCode: string;
  shareUrl?: string;
  firstLevelRatioBp: number;
  baseFirstLevelRatioBp: number;
  browseCount: number;
  registerCount: number;
  rechargeCount: number;
  rechargeUserCount: number;
  rechargeAmount: number;
  totalCommission: number;
  availableBalance: number;
  pendingWithdrawal: number;
  rewardedCount: number;
  rewardPoints: number;
  dailySummaries: MemberReferralDailySummary[];
  recentInvites: DistributorRegisterDetail[];
}

export interface MemberReferralDailySummary {
  date: string;
  browseUserCount: number;
  browseCount: number;
  registerCount: number;
  rechargeUserCount: number;
  rechargeOrderCount: number;
  rechargeAmount: number;
  commissionAmount: number;
}

let mediaDomainCache = "";
let homeConfigPromise: Promise<HomeDisplayConfig> | null = null;

function isAbsoluteURL(value: string): boolean {
  return /^(?:[a-z][a-z\d+\-.]*:)?\/\//i.test(value) || value.startsWith("data:") || value.startsWith("blob:");
}

function isSiteAssetURL(value: string): boolean {
  return value.startsWith("/images/") || value.startsWith("/assets/");
}

function joinMediaURL(value?: string): string {
  const raw = (value || "").trim();
  if (!raw || isAbsoluteURL(raw) || raw.startsWith("#") || isSiteAssetURL(raw)) {
    return raw;
  }
	if (raw.startsWith("/api/")) {
		return resolveApiUrl(raw);
	}

  const domain = mediaDomainCache.trim().replace(/\/+$/, "");
  if (!domain) {
    return raw;
  }
  return `${domain}/${raw.replace(/^\/+/, "")}`;
}

export function resolveMediaURL(value?: string): string {
  return joinMediaURL(value);
}

function normalizeDramaEpisode(episode?: DramaEpisode): DramaEpisode | undefined {
  if (!episode) {
    return episode;
  }
  return {
    ...episode,
    coverUrl: joinMediaURL(episode.coverUrl),
    videoUrl: joinMediaURL(episode.videoUrl),
  };
}

function normalizeDramaCard<T extends DramaCard>(drama: T): T {
  const currentEpisode = normalizeDramaEpisode(drama.currentEpisode);
  return {
    ...drama,
    coverUrl: joinMediaURL(drama.coverUrl),
    carouselImageUrl: joinMediaURL(drama.carouselImageUrl),
    ...(currentEpisode ? { currentEpisode } : {}),
  };
}

function normalizeDramaDetail(detail: DramaDetail): DramaDetail {
  return {
    ...normalizeDramaCard(detail),
    episodes: (detail.episodes || []).map((episode) => normalizeDramaEpisode(episode) as DramaEpisode),
  };
}

function normalizeLibrary(library: MemberDramaLibrary): MemberDramaLibrary {
  return {
    recent: (library.recent || []).map(normalizeDramaCard),
    favorites: (library.favorites || []).map(normalizeDramaCard),
  };
}

function normalizeCreatorCenter(data: CreatorCenterData): CreatorCenterData {
  return {
    ...data,
    works: (data.works || []).map((work) => ({
      ...work,
      coverUrl: joinMediaURL(work.coverUrl),
    })),
  };
}

async function ensureMediaDomainLoaded(): Promise<void> {
  if (mediaDomainCache) {
    return;
  }

  for (let attempt = 0; attempt < 3 && !mediaDomainCache; attempt += 1) {
    try {
      await fetchHomeDisplayConfig();
    } catch {
      if (attempt < 2) {
        await new Promise((resolve) => window.setTimeout(resolve, 250 * (attempt + 1)));
      }
    }
  }
}

async function request<T>(url: string, init?: RequestInit): Promise<T> {
  const headers = new Headers(init?.headers);
  try {
    headers.set("Accept-Language", window.localStorage.getItem("cinema:language") || document.documentElement.lang || "zh-CN");
  } catch {
    headers.set("Accept-Language", document.documentElement.lang || "zh-CN");
  }
  const identityHeaders = await clientFingerprintHeaders();
  Object.entries(identityHeaders).forEach(([key, value]) => headers.set(key, value));
  const response = await fetch(resolveApiUrl(url), { ...init, headers });
  const payload = (await response.json()) as Partial<ApiEnvelope<T>>;

  if (!response.ok || payload.code !== 0) {
    throw new ApiError(payload.message || "请求失败，请稍后重试", response.status);
  }

  return payload.data as T;
}

export function isUnauthorizedError(error: unknown): boolean {
  return error instanceof ApiError && error.status === 401;
}

export function isInsufficientPointsError(error: unknown): boolean {
  if (!(error instanceof ApiError)) {
    return false;
  }
  const message = error.message.toLowerCase();
  return message.includes("insufficient points") || message.includes("积分不足");
}

export async function fetchMemberProfile(token: string): Promise<MemberProfile> {
  return request<MemberProfile>("/api/member/me", {
    headers: buildHeaders(token),
  });
}

export async function fetchCreatorCenter(token: string): Promise<CreatorCenterData> {
  await ensureMediaDomainLoaded();
  const data = await request<CreatorCenterData>("/api/member/creator", { headers: buildHeaders(token) });
  return normalizeCreatorCenter(data);
}

export async function applyForCreator(token: string): Promise<CreatorCenterData> {
  await ensureMediaDomainLoaded();
  const data = await request<CreatorCenterData>("/api/member/creator/apply", { method: "POST", headers: buildHeaders(token) });
  return normalizeCreatorCenter(data);
}

export async function submitCreatorWork(draft: CreatorDraft, token: string): Promise<CreatorWork> {
  return request<CreatorWork>("/api/member/creator/works", {
    method: "POST",
    headers: buildHeaders(token),
    body: JSON.stringify(draft),
  });
}

export async function uploadCreatorCover(file: File, token: string): Promise<{ url: string }> {
  const formData = new FormData();
  formData.append("file", file);
  const headers: HeadersInit = { Authorization: `Bearer ${token}`, ...nativeAppRequestHeaders() };
  const result = await request<{ url: string }>("/api/member/creator/cover", {
    method: "POST",
    headers,
    body: formData,
  });
  await ensureMediaDomainLoaded();
  return result;
}

export async function uploadCreatorEpisode(file: File, token: string): Promise<{ url: string }> {
  const formData = new FormData();
  formData.append("file", file);
  const headers: HeadersInit = { Authorization: `Bearer ${token}`, ...nativeAppRequestHeaders() };
  const result = await request<{ url: string }>("/api/member/creator/episode", {
    method: "POST",
    headers,
    body: formData,
  });
  return result;
}

export interface CreatorChunkInitParams {
  fileMd5: string;
  fileName: string;
  fileSize: number;
  chunkSize: number;
  totalChunks: number;
}

export interface CreatorChunkMergeParams {
  uploadId: string;
  fileMd5: string;
  fileName: string;
  fileSize: number;
  totalChunks: number;
}

export async function initCreatorEpisodeChunks(params: CreatorChunkInitParams, token: string, signal?: AbortSignal) {
  return request<{ uploadId: string; uploadedChunks: number[] }>("/api/member/creator/episode/chunk/init", {
    method: "POST",
    headers: buildHeaders(token),
    body: JSON.stringify(params),
    signal,
  });
}

export async function uploadCreatorEpisodeChunk(formData: FormData, token: string, signal?: AbortSignal) {
  return request<{ chunkIndex: number; received: boolean }>("/api/member/creator/episode/chunk/upload", {
    method: "POST",
    headers: { Authorization: `Bearer ${token}`, ...nativeAppRequestHeaders() },
    body: formData,
    signal,
  });
}

export async function mergeCreatorEpisodeChunks(params: CreatorChunkMergeParams, token: string, signal?: AbortSignal) {
	const result = await request<{ url: string; thumbnailUrl: string; id: number; name: string; size: number }>("/api/member/creator/episode/chunk/merge", {
    method: "POST",
    headers: buildHeaders(token),
    body: JSON.stringify(params),
    signal,
  });
  return result;
}

export async function cancelCreatorEpisodeChunks(uploadId: string, token: string) {
  return request<unknown>("/api/member/creator/episode/chunk/cancel", {
    method: "DELETE",
    headers: buildHeaders(token),
    body: JSON.stringify({ uploadId }),
  });
}

export async function updateMemberProfile(nickName: string, email: string, token: string): Promise<MemberProfile> {
  return request<MemberProfile>("/api/member/profile", {
    method: "PUT",
    headers: buildHeaders(token),
    body: JSON.stringify({ nickName, email }),
  });
}

export async function updateMemberPassword(newPassword: string, confirmPassword: string, token: string): Promise<{ updated: boolean }> {
  return request<{ updated: boolean }>("/api/member/profile/password", {
    method: "PUT",
    headers: buildHeaders(token),
    body: JSON.stringify({ newPassword, confirmPassword }),
  });
}

export async function fetchMemberPointLogs(token: string, pageNum = 1, pageSize = 30): Promise<MemberPointLogList> {
  const params = new URLSearchParams({ pageNum: String(pageNum), pageSize: String(pageSize) });
  return request<MemberPointLogList>(`/api/member/points/logs?${params.toString()}`, {
    headers: buildHeaders(token),
  });
}

export async function fetchDailyCheckInStatus(token: string): Promise<DailyCheckInStatus> {
  return request<DailyCheckInStatus>("/api/member/daily-check-in", {
    headers: buildHeaders(token),
  });
}

export async function claimDailyCheckIn(token: string): Promise<DailyCheckInStatus> {
  return request<DailyCheckInStatus>("/api/member/daily-check-in", {
    method: "POST",
    headers: buildHeaders(token),
  });
}

export async function fetchLimitedRechargeTaskStatus(token: string): Promise<LimitedRechargeTaskStatus> {
  return request<LimitedRechargeTaskStatus>("/api/member/limited-recharge-task", {
    headers: buildHeaders(token),
  });
}

export async function claimLimitedRechargeTask(token: string): Promise<LimitedRechargeTaskStatus> {
  return request<LimitedRechargeTaskStatus>("/api/member/limited-recharge-task/claim", {
    method: "POST",
    headers: buildHeaders(token),
  });
}

export async function fetchEmailBindingTaskStatus(token: string): Promise<EmailBindingTaskStatus> {
  return request<EmailBindingTaskStatus>("/api/member/email-binding-task", {
    headers: buildHeaders(token),
  });
}

export async function claimEmailBindingTask(token: string): Promise<EmailBindingTaskStatus> {
  return request<EmailBindingTaskStatus>("/api/member/email-binding-task/claim", {
    method: "POST",
    headers: buildHeaders(token),
  });
}

export async function fetchDailyUnlockTaskStatus(token: string): Promise<DailyUnlockTaskStatus> {
  return request<DailyUnlockTaskStatus>("/api/member/daily-unlock-tasks", {
    headers: buildHeaders(token),
  });
}

export async function claimDailyUnlockTask(token: string, taskCode: string): Promise<DailyUnlockTaskStatus> {
  return request<DailyUnlockTaskStatus>(`/api/member/daily-unlock-tasks/${encodeURIComponent(taskCode)}/claim`, {
    method: "POST",
    headers: buildHeaders(token),
  });
}

export async function fetchHomeDisplayConfig(): Promise<HomeDisplayConfig> {
  if (!homeConfigPromise) {
    homeConfigPromise = request<HomeDisplayConfig>("/api/home/config", {
      headers: buildHeaders(),
    }).then((config) => {
      mediaDomainCache = (config.mediaDomain || "").trim();
      return {
        ...config,
        adImage: joinMediaURL(config.adImage),
      };
    }).catch((error) => {
      homeConfigPromise = null;
      throw error;
    });
  }
  return homeConfigPromise;
}

export async function fetchAdvertisements(placement?: "content" | "home_nav" | "earn_recommend"): Promise<PublicAdvertisement[]> {
  await ensureMediaDomainLoaded();
  const query = placement ? `?placement=${encodeURIComponent(placement)}` : "";
  const items = await request<PublicAdvertisement[]>(`/api/ads${query}`, {
    headers: buildHeaders(),
  });
  return (items || []).map((item) => ({
    ...item,
    imageUrl: joinMediaURL(item.imageUrl),
  }));
}

export async function sendMemberEmailCode(email: string, scene = "login"): Promise<MemberEmailCodeResult> {
  return request<MemberEmailCodeResult>("/api/member/email/send-code", {
    method: "POST",
    headers: buildHeaders(),
    body: JSON.stringify({ email, scene }),
  });
}

export async function loginMemberByEmailCode(email: string, code: string, shareCode = ""): Promise<MemberLoginResult> {
  return request<MemberLoginResult>("/api/member/email/login", {
    method: "POST",
    headers: buildHeaders(),
    body: JSON.stringify({ email, code, shareCode }),
  });
}

export async function autoRegisterMember(shareCode = ""): Promise<MemberAutoRegisterResult> {
  return request<MemberAutoRegisterResult>("/api/member/auto/register", {
    method: "POST",
    headers: buildHeaders(),
    body: JSON.stringify({ shareCode }),
  });
}

export async function loginMemberByPassword(payload: {
  username: string;
  password: string;
  captchaId: string;
  captchaValue: string;
}): Promise<MemberLoginResult> {
  return request<MemberLoginResult>("/api/member/account/login", {
    method: "POST",
    headers: buildHeaders(),
    body: JSON.stringify(payload),
  });
}

export async function registerMemberByPassword(payload: {
  registerType: "account" | "email";
  username?: string;
  email?: string;
  code?: string;
  password: string;
  shareCode?: string;
  captchaId: string;
  captchaValue: string;
}): Promise<MemberLoginResult> {
  return request<MemberLoginResult>("/api/member/register", {
    method: "POST",
    headers: buildHeaders(),
    body: JSON.stringify(payload),
  });
}

export async function resetMemberPassword(payload: {
  username?: string;
  email: string;
  code: string;
  password: string;
  captchaId: string;
  captchaValue: string;
}): Promise<void> {
  return request<void>("/api/member/password/reset", {
    method: "POST",
    headers: buildHeaders(),
    body: JSON.stringify(payload),
  });
}

export async function loginMemberByFirebase(payload: {
  provider: string;
  id_token: string;
  access_token?: string;
  oauth_token_secret?: string;
  shareCode?: string;
}): Promise<MemberLoginResult> {
  return request<MemberLoginResult>("/api/member/firebase/login", {
    method: "POST",
    headers: buildHeaders(),
    body: JSON.stringify(payload),
  });
}

export async function fetchMemberCaptchaConfig(): Promise<CaptchaConfigResult> {
  return request<CaptchaConfigResult>("/api/config/get", {
    headers: buildHeaders(),
  });
}

export async function fetchMemberCaptchaImage(): Promise<CaptchaImageResult> {
  return request<CaptchaImageResult>("/api/captcha/verify", {
    headers: buildHeaders(),
  });
}

export interface DramaListQuery {
  categoryId?: number;
  categoryCode?: string;
  scene?: "trending";
}

export async function fetchDramaList(token?: string, query?: DramaListQuery): Promise<DramaCard[]> {
  await ensureMediaDomainLoaded();
  const params = new URLSearchParams();
  if (query?.categoryId) {
    params.set("categoryId", String(query.categoryId));
  }
  if (query?.categoryCode?.trim()) {
    params.set("categoryCode", query.categoryCode.trim());
  }
  if (query?.scene) {
    params.set("scene", query.scene);
  }
  const rows = await request<DramaCard[]>(params.size ? `/api/dramas?${params.toString()}` : "/api/dramas", {
    headers: buildHeaders(token),
    cache: "no-store",
  });
  return (rows || []).map(normalizeDramaCard);
}

export async function fetchDramaCategories(): Promise<DramaCategory[]> {
  return request<DramaCategory[]>("/api/dramas/categories", {
    headers: buildHeaders(),
    cache: "no-store",
  });
}

export async function recordHomeVisit(): Promise<void> {
  await request("/api/home/visit", {
    method: "POST",
    headers: buildHeaders(),
    body: JSON.stringify({ pageUrl: window.location.href.slice(0, 2048), sourcePage: document.referrer.slice(0, 2048) }),
  });
}

export async function fetchDramaDetail(dramaId: number, token?: string): Promise<DramaDetail> {
  await ensureMediaDomainLoaded();
  const detail = await request<DramaDetail>(`/api/dramas/${dramaId}`, {
    headers: buildHeaders(token),
    cache: "no-store",
  });
  return normalizeDramaDetail(detail);
}

export async function recordDramaView(dramaId: number): Promise<void> {
  await request<{ recorded: boolean }>(`/api/dramas/${dramaId}/view`, {
    method: "POST",
  });
}

export async function fetchMemberLibrary(token: string): Promise<MemberDramaLibrary> {
  await ensureMediaDomainLoaded();
  const library = await request<MemberDramaLibrary>("/api/member/library", {
    headers: buildHeaders(token),
  });
  return normalizeLibrary(library);
}

export async function setDramaFavorite(dramaId: number, favorite: boolean, token: string): Promise<void> {
  await request<{ favorite: boolean }>(`/api/member/dramas/${dramaId}/favorite`, {
    method: "POST",
    headers: buildHeaders(token),
    body: JSON.stringify({ favorite }),
  });
}

export async function removeRecentDrama(dramaId: number, token: string): Promise<void> {
  await request<{ removed: boolean }>(`/api/member/dramas/${dramaId}/recent`, {
    method: "DELETE",
    headers: buildHeaders(token),
  });
}

export async function recordDramaWatch(dramaId: number, episodeId: number, token: string): Promise<void> {
  await request<{ recorded: boolean }>(`/api/member/dramas/${dramaId}/watch`, {
    method: "POST",
    headers: buildHeaders(token),
    body: JSON.stringify({ episodeId }),
  });
}

export async function unlockDramaEpisode(dramaId: number, token: string): Promise<DramaUnlockResult> {
  await ensureMediaDomainLoaded();
  const result = await request<DramaUnlockResult>(`/api/member/dramas/${dramaId}/unlock`, {
    method: "POST",
    headers: buildHeaders(token),
  });
  return {
    ...result,
    drama: normalizeDramaDetail(result.drama),
  };
}

export async function clearRecentDramas(token: string): Promise<void> {
  await request<{ cleared: boolean }>("/api/member/dramas/recent", {
    method: "DELETE",
    headers: buildHeaders(token),
  });
}

export async function fetchMemberPurchases(token: string): Promise<DramaCard[]> {
  await ensureMediaDomainLoaded();
  const rows = await request<DramaCard[]>("/api/member/purchases", {
    headers: buildHeaders(token),
  });
  return (rows || []).map(normalizeDramaCard);
}

const refreshTokenRequests = new Map<string, Promise<RefreshTokenResult>>();

export function refreshMemberToken(refreshToken: string): Promise<RefreshTokenResult> {
  const existing = refreshTokenRequests.get(refreshToken);
  if (existing) return existing;

  const pending = request<RefreshTokenResult>("/api/refreshToken", {
    method: "POST",
    headers: buildHeaders(),
    body: JSON.stringify({ refreshToken }),
  }).finally(() => {
    if (refreshTokenRequests.get(refreshToken) === pending) {
      refreshTokenRequests.delete(refreshToken);
    }
  });
  refreshTokenRequests.set(refreshToken, pending);
  return pending;
}

export async function fetchMemberPaymentOptions(token: string): Promise<MemberPaymentOptions> {
  return request<MemberPaymentOptions>("/api/member/payment/options", {
    headers: buildHeaders(token),
  });
}

export async function createMemberRechargeOrder(
  packageId: string,
  payMethod: "alipay" | "wechat" | "usdt",
  token: string,
): Promise<MemberRechargeCreateResult> {
  return request<MemberRechargeCreateResult>("/api/member/payment/create", {
    method: "POST",
    headers: buildHeaders(token),
    body: JSON.stringify({ packageId, payMethod }),
  });
}

export async function queryMemberRechargeOrder(outTradeNo: string, token: string): Promise<MemberRechargeQueryResult> {
  return request<MemberRechargeQueryResult>("/api/member/payment/query", {
    method: "POST",
    headers: buildHeaders(token),
    body: JSON.stringify({ outTradeNo }),
  });
}

export async function logoutMember(accessToken: string, refreshToken: string): Promise<void> {
  await request<void>("/api/member/logout", {
    method: "POST",
    headers: buildHeaders(accessToken),
    body: JSON.stringify({ refreshToken }),
  });
}

export function isRefreshTokenRejected(error: unknown): boolean {
  return error instanceof ApiError && error.status === 401;
}

export async function createMemberPaymentTicket(
	packageId: string,
	payMethod: "alipay" | "wechat" | "usdt",
	token: string,
): Promise<MemberPaymentTicketResult> {
	return request<MemberPaymentTicketResult>("/api/member/payment/ticket", {
		method: "POST",
		headers: buildHeaders(token),
		body: JSON.stringify({ packageId, payMethod }),
	});
}

export async function fetchMemberRechargeOrders(
  token: string,
  params?: { pageNum?: number; pageSize?: number },
): Promise<{ list: MemberRechargeOrder[]; total: number }> {
  const searchParams = new URLSearchParams();
  searchParams.set("pageNum", String(params?.pageNum || 1));
  searchParams.set("pageSize", String(params?.pageSize || 20));
  return request<{ list: MemberRechargeOrder[]; total: number }>(
    `/api/member/payment/orders?${searchParams.toString()}`,
    { headers: buildHeaders(token) },
  );
}

export async function fetchDistributorProfile(token: string): Promise<DistributorProfile> {
  return request<DistributorProfile>("/api/member/distribution/profile", {
    headers: buildHeaders(token),
  });
}

export async function fetchMemberReferralOverview(
  token: string,
  params?: { startTime?: string; endTime?: string },
): Promise<MemberReferralOverview> {
  const searchParams = new URLSearchParams();
  if (params?.startTime) searchParams.set("startTime", params.startTime);
  if (params?.endTime) searchParams.set("endTime", params.endTime);
  const query = searchParams.toString();
  return request<MemberReferralOverview>(`/api/member/distribution/referral-overview${query ? `?${query}` : ""}`, {
    headers: buildHeaders(token),
  });
}

export async function fetchDistributorDashboard(
  token: string,
  params?: { startTime?: string; endTime?: string },
): Promise<DistributorDashboard> {
  const searchParams = new URLSearchParams();
  if (params?.startTime) {
    searchParams.set("startTime", params.startTime);
  }
  if (params?.endTime) {
    searchParams.set("endTime", params.endTime);
  }
  const query = searchParams.toString();
  return request<DistributorDashboard>(`/api/member/distribution/dashboard${query ? `?${query}` : ""}`, {
    headers: buildHeaders(token),
  });
}

function buildDistributionListQuery(params?: { startTime?: string; endTime?: string; pageNum?: number; pageSize?: number }) {
  const searchParams = new URLSearchParams();
  searchParams.set("pageNum", String(params?.pageNum || 1));
  searchParams.set("pageSize", String(params?.pageSize || 10));
  if (params?.startTime) {
    searchParams.set("startTime", params.startTime);
  }
  if (params?.endTime) {
    searchParams.set("endTime", params.endTime);
  }
  return searchParams.toString();
}

export async function fetchDistributorBrowseDetails(
  token: string,
  params?: { startTime?: string; endTime?: string; pageNum?: number; pageSize?: number },
): Promise<{ list: DistributorBrowseDetail[]; total: number }> {
  return request<{ list: DistributorBrowseDetail[]; total: number }>(
    `/api/member/distribution/browse-details?${buildDistributionListQuery(params)}`,
    {
      headers: buildHeaders(token),
    },
  );
}

export async function fetchDistributorRegisterDetails(
  token: string,
  params?: { startTime?: string; endTime?: string; pageNum?: number; pageSize?: number },
): Promise<{ list: DistributorRegisterDetail[]; total: number }> {
  return request<{ list: DistributorRegisterDetail[]; total: number }>(
    `/api/member/distribution/register-details?${buildDistributionListQuery(params)}`,
    {
      headers: buildHeaders(token),
    },
  );
}

export async function fetchDistributorRechargeDetails(
  token: string,
  params?: { startTime?: string; endTime?: string; pageNum?: number; pageSize?: number },
): Promise<{ list: DistributorRechargeDetail[]; total: number }> {
  return request<{ list: DistributorRechargeDetail[]; total: number }>(
    `/api/member/distribution/recharge-details?${buildDistributionListQuery(params)}`,
    {
      headers: buildHeaders(token),
    },
  );
}

export async function recordDistributorShareBrowse(payload: {
  shareCode: string;
  dramaId?: number;
  channel?: string;
  sourcePage?: string;
}): Promise<void> {
  const sourcePage = payload.sourcePage || (typeof window !== "undefined"
    ? document.referrer || `${window.location.origin}${window.location.pathname}${window.location.search}`
    : "");
  await request<{ recorded: boolean }>("/api/member/distribution/share/browse", {
    method: "POST",
    headers: buildHeaders(),
    body: JSON.stringify({
      shareCode: payload.shareCode,
      dramaId: payload.dramaId || 0,
      channel: payload.channel || "web",
      sourcePage,
    }),
  });
}

export async function fetchDistributorWithdrawals(
  token: string,
  params?: { pageNum?: number; pageSize?: number },
): Promise<{ list: DistributorWithdrawal[]; total: number }> {
  const searchParams = new URLSearchParams();
  searchParams.set("pageNum", String(params?.pageNum || 1));
  searchParams.set("pageSize", String(params?.pageSize || 10));
  return request<{ list: DistributorWithdrawal[]; total: number }>(
    `/api/member/distribution/withdrawals?${searchParams.toString()}`,
    {
      headers: buildHeaders(token),
    },
  );
}

export async function createDistributorWithdrawal(
  token: string,
  payload: { amount: number; method: string; account: string },
): Promise<DistributorWithdrawal> {
  return request<DistributorWithdrawal>("/api/member/distribution/withdrawals", {
    method: "POST",
    headers: buildHeaders(token),
    body: JSON.stringify(payload),
  });
}
